import type { OperationProgressModel } from './operationProgress.types';
import {
  selectOperationProgressWindowRequest,
  type OperationProgressWindowSelectionCandidate,
} from './operationProgressWindowRequestSelection';

export type OperationProgressWindowMaxWidth = 'xs' | 'sm' | 'md' | 'lg';

export interface OperationProgressWindowRequestInput {
  id: string;
  open: boolean;
  model: OperationProgressModel;
  priority?: number;
  onClose?: () => void;
  maxWidth?: OperationProgressWindowMaxWidth;
  /** Suppress the embedded window inside Revit's WebView2 (plugin-hosted window takes over). */
  suppressInWebView2?: boolean;
}

export interface RegisteredOperationProgressWindowRequest
  extends OperationProgressWindowRequestInput,
    OperationProgressWindowSelectionCandidate {
  priority: number;
  sequence: number;
}

export interface OperationProgressWindowRequestStore {
  subscribe: (listener: () => void) => () => void;
  getSnapshot: () => RegisteredOperationProgressWindowRequest | null;
  upsert: (request: OperationProgressWindowRequestInput) => void;
  remove: (id: string) => void;
}

export function createOperationProgressWindowRequestStore(): OperationProgressWindowRequestStore {
  const requests = new Map<string, RegisteredOperationProgressWindowRequest>();
  const listeners = new Set<() => void>();
  let sequence = 0;
  let activeRequest: RegisteredOperationProgressWindowRequest | null = null;

  const emitChange = (): void => {
    activeRequest = selectOperationProgressWindowRequest(requests.values());
    listeners.forEach((listener) => listener());
  };

  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    getSnapshot() {
      return activeRequest;
    },

    upsert(request) {
      if (!request.open) {
        requests.delete(request.id);
        emitChange();
        return;
      }

      sequence += 1;
      requests.set(request.id, {
        ...request,
        priority: request.priority ?? 0,
        sequence,
      });
      emitChange();
    },

    remove(id) {
      if (!requests.delete(id)) {
        return;
      }

      emitChange();
    },
  };
}
